import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useFetchWithAuth } from '../session/useFetchWithAuth'
import { EventAttachment } from './types'

const API_URL = import.meta.env.VITE_EPC_API_URL ?? ''

/**
 * Fetch all attachments for an event
 */
export const useEventAttachments = (eventId: string) => {
  const fetchWithAuth = useFetchWithAuth()

  return useQuery({
    queryKey: ['eventAttachments', eventId],
    queryFn: async (): Promise<EventAttachment[]> => {
      if (!eventId) return []

      try {
        const data = await fetchWithAuth(
          `${API_URL}/events/${eventId}/attachments`,
          {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
          }
        )
        return data.attachments || []
      } catch (error) {
        console.error('Error fetching event attachments:', error)
        throw error
      }
    },
    enabled: !!eventId,
  })
}

/**
 * Add an attachment to an event
 */
export const useAddAttachment = () => {
  const queryClient = useQueryClient()
  const fetchWithAuth = useFetchWithAuth()

  return useMutation({
    mutationFn: async ({
      eventId,
      attachment,
    }: {
      eventId: string
      attachment: Omit<EventAttachment, 'position'>
    }) => {
      return await fetchWithAuth(`${API_URL}/events/${eventId}/attachments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(attachment),
      })
    },
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({ queryKey: ['eventAttachments', eventId] })
    },
  })
}

/**
 * Delete an attachment from an event
 */
export const useDeleteAttachment = () => {
  const queryClient = useQueryClient()
  const fetchWithAuth = useFetchWithAuth()

  return useMutation({
    mutationFn: async ({
      eventId,
      attachmentId,
    }: {
      eventId: string
      attachmentId: string
    }) => {
      return await fetchWithAuth(
        `${API_URL}/events/${eventId}/attachments/${attachmentId}`,
        {
          method: 'DELETE',
        }
      )
    },
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({ queryKey: ['eventAttachments', eventId] })
    },
  })
}

/**
 * Update attachment order for an event
 */
export const useUpdateAttachmentOrder = () => {
  const queryClient = useQueryClient()
  const fetchWithAuth = useFetchWithAuth()

  return useMutation({
    mutationFn: async ({
      eventId,
      attachments,
    }: {
      eventId: string
      attachments: EventAttachment[]
    }) => {
      return await fetchWithAuth(`${API_URL}/events/${eventId}/attachments`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ attachments }),
      })
    },
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({ queryKey: ['eventAttachments', eventId] })
    },
  })
}
